import navigationConfig from 'configs/navigation.config'

/**
 * Plain function (not a hook) that returns the first permitted route path
 * for a given userInfo object. Can be called from callbacks, .then(), etc.
 *
 * @param {object} userInfo - { authority: [], permissions: [] | string, ... }
 * @returns {string} path – e.g. '/home', '/activity-board', '/select-project'
 */
export default function getFirstPermittedRoute(userInfo) {
    const authority = userInfo?.authority || []
    const isSuperAdmin = Array.isArray(authority) && authority.includes('SUPERADMIN')
    const isUserAdmin =
        Array.isArray(authority) &&
        (authority.includes('USERADMIN') || authority.includes('Admin'))

    // Admin roles always go to /home
    if (isSuperAdmin || isUserAdmin) {
        return '/home'
    }

    // Parse permissions (same logic as VerticalMenuContent's checkMenuPermission)
    let parsedPermissions = []
    const permissions = userInfo?.permissions

    try {
        if (permissions && permissions !== 'null') {
            if (typeof permissions === 'string') {
                const parsed = JSON.parse(permissions)
                if (Array.isArray(parsed)) {
                    parsedPermissions = parsed
                }
            } else if (Array.isArray(permissions) && permissions.length > 0) {
                const firstItem = permissions[0]
                if (typeof firstItem === 'string' && firstItem !== 'null') {
                    const parsed = JSON.parse(firstItem)
                    if (Array.isArray(parsed)) {
                        parsedPermissions = parsed
                    }
                } else if (Array.isArray(firstItem)) {
                    parsedPermissions = firstItem
                }
            }
        }
    } catch (e) {
        console.error('getFirstPermittedRoute: error parsing permissions', e)
    }

    // Helper: check if a single nav item is permitted
    const isPermitted = (nav) => {
        // Authority check – if the item requires a specific role the user doesn't have, skip
        if (nav.authority && Array.isArray(nav.authority) && nav.authority.length > 0) {
            const hasRequired = nav.authority.some((role) => authority.includes(role))
            if (!hasRequired) return false
        }

        // Module permission check via parsed permissions array
        if (nav.title && parsedPermissions.length > 0) {
            const mod = parsedPermissions.find((p) => p && p.name === nav.title)
            if (mod) {
                const val = String(mod.value)
                return val === '1' || val === '2'
            }
        }

        // Fallback: old numeric permission system
        if (nav.permission !== undefined && permissions && Array.isArray(permissions)) {
            let ok = false
            permissions.forEach((p) => {
                if (typeof p === 'number' && p >= nav.permission) {
                    ok = true
                }
            })
            return ok
        }

        return false
    }

    // Walk through navigationConfig in order and return the first permitted path
    for (const nav of navigationConfig) {
        // Top-level item
        if (nav.path && nav.path.startsWith('/') && isPermitted(nav)) {
            return nav.path
        }
        // Collapse item – check subMenu
        if (nav.subMenu && nav.subMenu.length > 0) {
            for (const sub of nav.subMenu) {
                if (sub.path && sub.path.startsWith('/') && isPermitted(sub)) {
                    return sub.path
                }
            }
        }
    }

    // Absolute fallback – send to select-project (everyone can access this)
    return '/select-project'
}
